// Tiny i18n module for ChatOlam agent UI.
// Flat string keys, no nesting in lookups (e.g. 'common.save').
// Locales: 'en' (default), 'he' (RTL).
//
// Usage:
//   import { t, setLocale, getLocale, useLocale } from '../lib/i18n';
//   t('common.save')              -> 'Save'
//   t('inbox.unread', { n: 3 })   -> '3 unread'
//
// React integration: useLocale() subscribes to changes so components
// re-render when the language switches.

import { useEffect, useState } from 'react';

export type Locale = 'en' | 'he';

const LS_KEY = 'chatolam.locale';

function detectInitial(): Locale {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
    if (stored === 'en' || stored === 'he') return stored;
  } catch {
    // ignore
  }
  try {
    const nav = typeof navigator !== 'undefined' ? navigator.language : '';
    if (nav && nav.toLowerCase().startsWith('he')) return 'he';
  } catch {
    // ignore
  }
  return 'en';
}

let currentLocale: Locale = detectInitial();
const listeners = new Set<() => void>();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale): void {
  if (locale === currentLocale) return;
  currentLocale = locale;
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(LS_KEY, locale);
  } catch {
    // ignore
  }
  try {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
    }
  } catch {
    // ignore
  }
  listeners.forEach((fn) => fn());
}

// Apply dir/lang on first import so it's correct even before any component renders.
try {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = currentLocale;
    document.documentElement.dir = currentLocale === 'he' ? 'rtl' : 'ltr';
  }
} catch {
  // ignore
}

export function isRtl(): boolean {
  return currentLocale === 'he';
}

const STRINGS: Record<Locale, Record<string, string>> = {
  en: {
    // common buttons
    'common.save': 'Save',
    'common.cancel': 'Cancel',
    'common.delete': 'Delete',
    'common.confirm': 'Confirm',
    'common.edit': 'Edit',
    'common.create': 'Create',
    'common.new': 'New',
    'common.add': 'Add',
    'common.remove': 'Remove',
    'common.close': 'Close',
    'common.back': 'Back',
    'common.next': 'Next',
    'common.previous': 'Previous',
    'common.skip': 'Skip',
    'common.finish': 'Finish',
    'common.refresh': 'Refresh',
    'common.loading': 'Loading…',
    'common.noData': 'No data',
    'common.yes': 'Yes',
    'common.no': 'No',
    'common.enabled': 'Enabled',
    'common.disabled': 'Disabled',
    'common.name': 'Name',
    'common.description': 'Description',
    'common.actions': 'Actions',
    'common.search': 'Search',
    'common.copy': 'Copy',
    'common.copied': 'Copied',
    'common.error': 'Error',
    'common.warning': 'Warning',
    'common.success': 'Success',
    'common.required': 'Required',
    'common.optional': 'Optional',
    // confirm prompts
    'confirm.delete': 'Delete this item?',
    'confirm.discard': 'Discard changes?',
    // TopBar / shell
    'topbar.title': 'ChatOlam Agent',
    'topbar.inbox': 'Inbox',
    'topbar.reports': 'Reports',
    'topbar.settings': 'Settings',
    'topbar.signOut': 'Sign out',
    'topbar.language': 'Language',
    'topbar.languageEn': 'English',
    'topbar.languageHe': 'עברית',
    // Inbox tabs
    'inbox.tab.mine': 'Mine',
    'inbox.tab.unassigned': 'Unassigned',
    'inbox.tab.all': 'All',
    'inbox.tab.open': 'Open',
    'inbox.tab.pending': 'Pending',
    'inbox.tab.snoozed': 'Snoozed',
    'inbox.tab.resolved': 'Resolved',
    'inbox.unread': '{n} unread',
    // Known-issue notice (F-4) — the operator's "we already know" banner above
    // the queue. Agents get it as well as customers: the whole point is that
    // nobody re-diagnoses an incident support already declared.
    'inbox.knownIssue.label': 'Known issue',
    'inbox.knownIssue.botTelling': 'The bot is raising this with customers whose question relates to it.',
    'inbox.knownIssue.more': '+{n} more active',
    'inbox.knownIssue.dismiss': 'Hide',
    'inbox.knownIssue.dismissTitle': 'Hide until the next reload — it comes back while the issue is still open',
    'inbox.knownIssue.manage': 'Manage',
    // MessageThread — "bad answer" correction popover. Two destinations: a
    // corrected ANSWER goes to support memory (replayable to a customer), a
    // RULE becomes a draft playbook that needs approval before it affects the bot.
    'thread.fix.title': 'Fix this answer & teach the AI',
    'thread.fix.mode.answer': 'Right answer',
    'thread.fix.mode.rule': "What's wrong (rule)",
    'thread.fix.answerPlaceholder': 'Write the correct answer…',
    'thread.fix.rulePlaceholder': 'Describe what the bot did wrong and the rule it should follow next time…',
    'thread.fix.ruleHint': 'Saved as a DRAFT rule. It only affects the bot after someone approves it in Settings, under "Support flows".',
    'thread.fix.saveAnswer': 'Save correction & teach AI',
    'thread.fix.saveRule': 'Save as a rule',
    'thread.fix.flagOnly': 'Flag only',
    'thread.fix.flagOnlyHint': 'Mark bad without writing a correction',
    'thread.fix.answerSaved': 'Marked bad + taught the corrected answer to the AI',
    'thread.fix.answerFailed': 'Could not save the correction',
    'thread.fix.ruleSaved': 'Saved as a draft rule — approve it in Settings, under "Support flows"',
    // A draft the server could not derive a trigger for matches NO customer
    // turn, so approving it changes nothing. Saying only "approve it" there is
    // how an agent walks away believing the bot was taught something. Both
    // remaining steps, in order, and the screen that does them.
    'thread.fix.ruleSavedNoTrigger':
      'Saved as a draft rule — but nothing triggers it yet, so as it stands it can never fire. Two steps left in Settings → "Support flows": add a trigger (an intent or a keyword), then approve it.',
    'thread.fix.ruleFailed': 'Could not save the rule',
    'thread.fix.openFlows': 'Open Support flows',
    // MessageThread — "save to AI memory" toggle.
    'thread.memory.saved': 'Saved to AI memory — the bot will reuse it',
    'thread.memory.saveFailed': 'Could not save to AI memory',
    'thread.memory.removed': 'Removed from AI memory',
    'thread.memory.removeFailed': 'Could not remove from AI memory',
    // MessageThread — support-memory guard. A rejection used to surface as a
    // bare "could not save" with no reason, which is how an operator burned a
    // morning re-clicking Save on an answer the server would never accept.
    // The hard block MUST name the way through (teach it as a rule).
    'thread.guard.block.toast': 'Not saved — this answer asks the customer for identity details. Teach it as a rule instead.',
    'thread.guard.block.title': 'Not saved: this answer asks the customer for identity details',
    'thread.guard.block.body':
      'A saved answer is replayed to the NEXT customer as an already-verified answer. An email address or card digits typed into a chat prove nothing, so this would just ask a different person for their details.',
    'thread.guard.block.remedy':
      'Teach it as a rule instead: a rule changes how the bot behaves and is never repeated to a customer word-for-word.',
    'thread.guard.block.switchToRule': 'Write it as a rule',
    // Shown instead of the button where there is no per-message rule editor to
    // switch to (an agent's own reply). Names the destination so the remedy is
    // still actionable rather than advice with nowhere to go.
    'thread.guard.block.remedyWhere': 'Write it in Settings, under "Support flows".',
    'thread.guard.warn.toast': 'Saved — note: it sends the customer to the personal area. Flagged for review.',
    'thread.guard.warn.title': 'Saved — with a note',
    'thread.guard.warn.body':
      'This answer points the customer at the personal area. That is exactly right when the setting really lives there, and a brush-off when it does not, so the save went through and was flagged for review.',
    'thread.guard.dismiss': 'Got it',
    // SettingsPage nav
    'settings.title': 'Settings',
    'settings.backToInbox': 'Back to Inbox',
    'settings.nav.inboxes': 'Inboxes',
    'settings.nav.aiMode': 'AI mode',
    'settings.nav.teams': 'Teams',
    'settings.nav.labels': 'Labels',
    'settings.nav.canned': 'Canned',
    'settings.nav.macros': 'Macros',
    'settings.nav.automations': 'Automations',
    'settings.nav.flows': 'Support flows',
    'settings.nav.knownIssues': 'Known issues',
    'settings.nav.agentAccess': 'Agent access',
    'settings.nav.sla': 'SLA',
    'settings.nav.bots': 'Bots',
    'settings.nav.webhooks': 'Webhooks',
    'settings.nav.apiTokens': 'API Tokens',
    'settings.nav.audit': 'Audit Log',
    'settings.nav.workingHours': 'Working Hours',
    'settings.nav.assignment': 'Auto-assignment',
    'settings.nav.webhookDeliveries': 'Webhook Deliveries',
    'settings.nav.security': 'Security',
    'settings.nav.info': 'Info',
    'settings.nav.overview': 'Overview',
    // Working hours
    'settings.workingHours.title': 'Working Hours',
    'settings.workingHours.selectInbox': 'Select inbox',
    'settings.workingHours.timezone': 'Timezone',
    'settings.workingHours.schedule': 'Weekly schedule',
    'settings.workingHours.holidays': 'Holidays',
    'settings.workingHours.addHoliday': 'Add holiday',
    'settings.workingHours.offlineMessage': 'Offline message',
    'settings.workingHours.open': 'Open',
    'settings.workingHours.close': 'Close',
    'settings.workingHours.day.mon': 'Monday',
    'settings.workingHours.day.tue': 'Tuesday',
    'settings.workingHours.day.wed': 'Wednesday',
    'settings.workingHours.day.thu': 'Thursday',
    'settings.workingHours.day.fri': 'Friday',
    'settings.workingHours.day.sat': 'Saturday',
    'settings.workingHours.day.sun': 'Sunday',
    // SLA
    'settings.sla.title': 'SLA configuration',
    'settings.sla.firstResponse': 'First response target (min)',
    'settings.sla.resolution': 'Resolution target (min)',
    'settings.sla.multipliers': 'Priority multipliers',
    'settings.sla.priority.urgent': 'Urgent',
    'settings.sla.priority.high': 'High',
    'settings.sla.priority.medium': 'Medium',
    'settings.sla.priority.low': 'Low',
    // Assignment
    'settings.assignment.title': 'Auto-assignment',
    'settings.assignment.strategy': 'Strategy',
    'settings.assignment.strategy.roundRobin': 'Round robin',
    'settings.assignment.strategy.leastLoaded': 'Least loaded',
    'settings.assignment.strategy.pinned': 'Pinned to agent',
    'settings.assignment.pinnedAgent': 'Pinned agent',
    'settings.assignment.skills': 'Skills',
    'settings.assignment.skills.placeholder': 'comma-separated tags',
    // Automations
    'settings.automations.title': 'Automations',
    'settings.automations.trigger': 'Trigger',
    'settings.automations.conditions': 'Conditions',
    'settings.automations.actions': 'Actions',
    'settings.automations.addCondition': 'Add condition',
    'settings.automations.addAction': 'Add action',
    'settings.automations.event.created': 'Conversation created',
    'settings.automations.event.message': 'Message received',
    'settings.automations.event.status': 'Status changed',
    'settings.automations.event.labels': 'Labels changed',
    // Webhook deliveries
    'settings.webhookDeliveries.title': 'Webhook deliveries',
    'settings.webhookDeliveries.time': 'Time',
    'settings.webhookDeliveries.url': 'URL',
    'settings.webhookDeliveries.event': 'Event',
    'settings.webhookDeliveries.status': 'Status',
    'settings.webhookDeliveries.code': 'HTTP code',
    'settings.webhookDeliveries.duration': 'Duration (ms)',
    'settings.webhookDeliveries.loadMore': 'Load more',
    // Security
    'settings.security.title': 'Security',
    'settings.security.require2fa': 'Require 2FA for all agents',
    'settings.security.idleTimeout': 'Auto-sign-out after N minutes idle',
    'settings.security.idleTimeoutHint': 'Default 60 minutes. 0 disables auto sign-out.',
    'settings.security.backendMissing': 'Backend route not yet deployed.',
    // Known issues (F-4) — admin declares a live incident; the bot stops
    // troubleshooting it and every agent sees it above the queue.
    'settings.knownIssues.title': 'Known issues',
    'settings.knownIssues.intro':
      'Declare an incident that is happening right now ("SMS to Israeli numbers is delayed"). While it is live the bot raises it with customers whose question relates to it instead of walking them through troubleshooting that cannot work, and every agent sees it above their queue.',
    'settings.knownIssues.new': '+ Declare an issue',
    'settings.knownIssues.edit': 'Edit',
    'settings.knownIssues.fieldTitle': 'Title',
    'settings.knownIssues.fieldTitlePlaceholder': 'e.g. SMS to Israeli numbers is delayed',
    'settings.knownIssues.fieldMessage': 'What to tell customers',
    'settings.knownIssues.fieldMessagePlaceholder':
      'e.g. Texts to 05x numbers are arriving late. We are on it — no need to re-send or reset anything.',
    'settings.knownIssues.fieldTitleHe': 'Title (Hebrew)',
    'settings.knownIssues.fieldMessageHe': 'What to tell customers (Hebrew)',
    'settings.knownIssues.hebrewHint':
      'Optional. Hebrew-speaking customers get this wording; leave it empty and they get the text above.',
    'settings.knownIssues.fieldEndsAt': 'Ends at (optional)',
    'settings.knownIssues.endsAtHint':
      'Leave empty for an open-ended incident — it then runs until someone marks it resolved.',
    'settings.knownIssues.save': 'Declare issue',
    'settings.knownIssues.saveEdit': 'Save changes',
    'settings.knownIssues.colIssue': 'Issue',
    'settings.knownIssues.colWindow': 'Window',
    'settings.knownIssues.colStatus': 'Status',
    'settings.knownIssues.statusLive': 'Live',
    'settings.knownIssues.statusScheduled': 'Scheduled',
    'settings.knownIssues.statusEnded': 'Window closed',
    'settings.knownIssues.statusResolved': 'Resolved',
    'settings.knownIssues.resolve': 'Mark resolved',
    'settings.knownIssues.resolveConfirm': 'Mark “{title}” resolved? The bot stops mentioning it immediately.',
    'settings.knownIssues.reactivate': 'Reactivate',
    'settings.knownIssues.windowOpen': 'From {from}, open-ended',
    'settings.knownIssues.windowUntil': 'From {from} until {to}',
    'settings.knownIssues.liveCount': '{n} live now — the bot is telling matching customers about it.',
    'settings.knownIssues.empty': 'No issues declared. The bot answers as if everything is healthy.',
    'settings.knownIssues.required': 'A title and a message are both required.',
    'settings.knownIssues.invalidWindow': 'The end time has to be later than the start.',
    'settings.knownIssues.pastWindow': 'That end time has already passed, so nobody would ever see the notice.',
    'settings.knownIssues.backendMissing':
      'This server has no known-issues route yet, so nothing can be declared from here until it is updated.',
    'settings.knownIssues.readOnly': 'Only an admin can declare or resolve a known issue.',
    // Branding (inbox)
    'settings.branding.logo': 'Logo URL',
    'settings.branding.primaryColor': 'Primary color',
    'settings.branding.brandName': 'Brand name',
    // Onboarding
    'onboarding.title': 'Welcome to ChatOlam',
    'onboarding.step.tenant': 'Tenant',
    'onboarding.step.admin': 'Admin',
    'onboarding.step.inbox': 'Inbox',
    'onboarding.step.seed': 'Content',
    'onboarding.step.snippet': 'Widget',
    'onboarding.tenant.code': 'Tenant code',
    'onboarding.tenant.name': 'Tenant name',
    'onboarding.admin.email': 'Primary admin email',
    'onboarding.inbox.name': 'Default inbox name',
    'onboarding.seed.cta': 'Seed canned responses + FAQ',
    'onboarding.snippet.title': 'Widget snippet',
    'onboarding.snippet.copy': 'Copy snippet',
    'onboarding.done': 'Done',
    // Reports
    'reports.title': 'Reports',
    'reports.range.7': 'Last 7 days',
    'reports.range.30': 'Last 30 days',
    'reports.range.90': 'Last 90 days',
    'reports.volume.title': 'Conversation volume',
    'reports.volume.total': 'Total',
    'reports.volume.open': 'Open',
    'reports.volume.pending': 'Pending',
    'reports.volume.resolved': 'Resolved',
    'reports.volume.byChannel': 'By channel',
    'reports.volume.byPriority': 'By priority',
    'reports.agents.title': 'Agent performance',
    'reports.agents.agent': 'Agent',
    'reports.agents.assigned': 'Assigned',
    'reports.agents.resolved': 'Resolved',
    'reports.agents.avgFirst': 'Avg first response (min)',
    'reports.agents.avgResolution': 'Avg resolution (min)',
    'reports.csat.title': 'Customer satisfaction (CSAT)',
    'reports.csat.responses': 'Responses',
    'reports.csat.avg': 'Average score',
    'reports.csat.fiveStar': '5-star count',
    'reports.csat.nps': 'NPS',
    'reports.csat.avgLen': 'Average comment length',
    'reports.csat.recentComments': 'Recent comments',
    'reports.export': 'Export conversations (NDJSON)',
    'reports.histogram.title': 'Time-to-resolve',
    'reports.histogram.lt1h': '< 1h',
    'reports.histogram.1to4h': '1-4h',
    'reports.histogram.4to24h': '4-24h',
    'reports.histogram.1to3d': '1-3d',
    'reports.histogram.gt3d': '> 3d',
    'reports.intent.title': 'Intent breakdown',
    // Contacts directory — anonymity filter. A widget visitor with no trusted
    // identifier gets a throwaway contact per session, so these rows pile up at
    // the top of a "last activity" sort and bury the reachable people.
    'contacts.anon.show': 'Show anonymous',
    'contacts.anon.hide': 'Hide anonymous',
    'contacts.anon.showing': 'Anonymous visitors shown',
    'contacts.anon.hiddenCount': '{n} anonymous hidden',
    'contacts.anon.hiddenSome': 'Hiding {n} anonymous visitor{s} — one-off widget sessions with no email or phone.',
    'contacts.anon.hiddenUnknown': 'Anonymous visitors are hidden — one-off widget sessions with no email or phone.',
    'contacts.anon.badge': 'anonymous',
    'contacts.anon.notReady':
      'Anonymous visitors could not be separated yet (contacts have no anonymity marker), so every contact is listed.',
    'contacts.empty.noMatch': 'No contacts match your filters.',
    'contacts.empty.allAnon':
      'No named contacts match. {n} anonymous visitor{s} are hidden — show them, or clear the filters.',
    'contacts.empty.clearFilters': 'Clear filters',
    'contacts.filter.anonymous': 'Anonymous',
  },
  he: {
    'common.save': 'שמירה',
    'common.cancel': 'ביטול',
    'common.delete': 'מחיקה',
    'common.confirm': 'אישור',
    'common.edit': 'עריכה',
    'common.create': 'יצירה',
    'common.new': 'חדש',
    'common.add': 'הוספה',
    'common.remove': 'הסרה',
    'common.close': 'סגור',
    'common.back': 'חזרה',
    'common.next': 'הבא',
    'common.previous': 'הקודם',
    'common.skip': 'דלג',
    'common.finish': 'סיים',
    'common.refresh': 'רענון',
    'common.loading': 'טוען…',
    'common.noData': 'אין נתונים',
    'common.yes': 'כן',
    'common.no': 'לא',
    'common.enabled': 'מופעל',
    'common.disabled': 'כבוי',
    'common.name': 'שם',
    'common.description': 'תיאור',
    'common.actions': 'פעולות',
    'common.search': 'חיפוש',
    'common.copy': 'העתק',
    'common.copied': 'הועתק',
    'common.error': 'שגיאה',
    'common.warning': 'אזהרה',
    'common.success': 'הצלחה',
    'common.required': 'חובה',
    'common.optional': 'לא חובה',
    'confirm.delete': 'למחוק את הפריט?',
    'confirm.discard': 'לבטל את השינויים?',
    'topbar.title': 'ChatOlam',
    'topbar.inbox': 'תיבה נכנסת',
    'topbar.reports': 'דוחות',
    'topbar.settings': 'הגדרות',
    'topbar.signOut': 'יציאה',
    'topbar.language': 'שפה',
    'topbar.languageEn': 'English',
    'topbar.languageHe': 'עברית',
    'inbox.tab.mine': 'שלי',
    'inbox.tab.unassigned': 'לא משויך',
    'inbox.tab.all': 'הכל',
    'inbox.tab.open': 'פתוח',
    'inbox.tab.pending': 'ממתין',
    'inbox.tab.snoozed': 'נדחה',
    'inbox.tab.resolved': 'נסגר',
    'inbox.unread': '{n} לא נקראו',
    // תקלה ידועה (F-4) — ההודעה של המפעיל "אנחנו כבר יודעים", מעל התור
    'inbox.knownIssue.label': 'תקלה ידועה',
    'inbox.knownIssue.botTelling': 'הבוט מספר על כך ללקוחות ששאלתם קשורה לתקלה.',
    'inbox.knownIssue.more': 'ועוד {n} פעילות',
    'inbox.knownIssue.dismiss': 'הסתרה',
    'inbox.knownIssue.dismissTitle': 'הסתרה עד הרענון הבא — ההודעה תחזור כל עוד התקלה פתוחה',
    'inbox.knownIssue.manage': 'ניהול',
    'thread.fix.title': 'תיקון התשובה ולימוד ה-AI',
    'thread.fix.mode.answer': 'התשובה הנכונה',
    'thread.fix.mode.rule': 'מה השתבש (כלל)',
    'thread.fix.answerPlaceholder': 'כתבו את התשובה הנכונה…',
    'thread.fix.rulePlaceholder': 'תארו מה הבוט עשה לא נכון ואיזה כלל עליו לפעול לפיו בפעם הבאה…',
    'thread.fix.ruleHint': 'נשמר כטיוטת כלל. הוא ישפיע על הבוט רק לאחר אישור בהגדרות, במסך "תרחישי תמיכה".',
    'thread.fix.saveAnswer': 'שמירת התיקון ולימוד ה-AI',
    'thread.fix.saveRule': 'שמירה ככלל',
    'thread.fix.flagOnly': 'סימון בלבד',
    'thread.fix.flagOnlyHint': 'סימון כתשובה גרועה בלי לכתוב תיקון',
    'thread.fix.answerSaved': 'סומן כגרוע + התשובה המתוקנת נלמדה',
    'thread.fix.answerFailed': 'לא ניתן היה לשמור את התיקון',
    'thread.fix.ruleSaved': 'נשמר כטיוטת כלל — יש לאשר בהגדרות, במסך "תרחישי תמיכה"',
    'thread.fix.ruleSavedNoTrigger':
      'נשמר כטיוטת כלל — אבל שום דבר לא מפעיל אותו, ולכן במצבו הנוכחי הוא לא יופעל אף פעם. נשארו שני צעדים בהגדרות, במסך "תרחישי תמיכה": להוסיף טריגר (כוונה או מילת מפתח), ואז לאשר.',
    'thread.fix.ruleFailed': 'לא ניתן היה לשמור את הכלל',
    'thread.fix.openFlows': 'פתיחת "תרחישי תמיכה"',
    // MessageThread — שמירה לזיכרון ה-AI
    'thread.memory.saved': 'נשמר בזיכרון ה-AI — הבוט ישתמש בזה שוב',
    'thread.memory.saveFailed': 'לא ניתן היה לשמור בזיכרון ה-AI',
    'thread.memory.removed': 'הוסר מזיכרון ה-AI',
    'thread.memory.removeFailed': 'לא ניתן היה להסיר מזיכרון ה-AI',
    // MessageThread — מנגנון ההגנה על זיכרון התמיכה
    'thread.guard.block.toast': 'לא נשמר — התשובה מבקשת מהלקוח פרטי זיהוי. במקום זאת, לַמדו את זה ככלל.',
    'thread.guard.block.title': 'לא נשמר: התשובה מבקשת מהלקוח פרטי זיהוי',
    'thread.guard.block.body':
      'תשובה שנשמרת מוצגת ללקוח הבא כתשובה מאומתת. כתובת מייל או ספרות של כרטיס שנכתבות בצ׳אט לא מאמתות דבר, ולכן התשובה הזו פשוט תבקש פרטים מאדם אחר.',
    'thread.guard.block.remedy':
      'לַמדו את זה ככלל: כלל משנה את ההתנהגות של הבוט ולעולם לא נאמר ללקוח מילה במילה.',
    'thread.guard.block.switchToRule': 'ניסוח ככלל',
    'thread.guard.block.remedyWhere': 'אפשר לנסח את הכלל בהגדרות, תחת "תרחישי תמיכה".',
    'thread.guard.warn.toast': 'נשמר — לתשומת לבכם: התשובה מפנה את הלקוח לאזור האישי. סומן לבדיקה.',
    'thread.guard.warn.title': 'נשמר — עם הערה',
    'thread.guard.warn.body':
      'התשובה מפנה את הלקוח לאזור האישי. זה בדיוק נכון כשההגדרה באמת נמצאת שם, וזו התחמקות כשלא — ולכן השמירה בוצעה והרשומה סומנה לבדיקה.',
    'thread.guard.dismiss': 'הבנתי',
    'settings.title': 'הגדרות',
    'settings.backToInbox': 'חזרה לתיבה',
    'settings.nav.inboxes': 'תיבות',
    'settings.nav.aiMode': 'מצב AI',
    'settings.nav.teams': 'צוותים',
    'settings.nav.labels': 'תוויות',
    'settings.nav.canned': 'תשובות מוכנות',
    'settings.nav.macros': 'מאקרו',
    'settings.nav.automations': 'אוטומציות',
    'settings.nav.flows': 'תרחישי תמיכה',
    'settings.nav.knownIssues': 'תקלות ידועות',
    'settings.nav.agentAccess': 'הרשאות נציגים',
    'settings.nav.sla': 'SLA',
    'settings.nav.bots': 'בוטים',
    'settings.nav.webhooks': 'Webhooks',
    'settings.nav.apiTokens': 'מפתחות API',
    'settings.nav.audit': 'יומן ביקורת',
    'settings.nav.workingHours': 'שעות פעילות',
    'settings.nav.assignment': 'הקצאה אוטומטית',
    'settings.nav.webhookDeliveries': 'משלוחי Webhook',
    'settings.nav.security': 'אבטחה',
    'settings.nav.info': 'מידע',
    'settings.nav.overview': 'סקירה',
    'settings.workingHours.title': 'שעות פעילות',
    'settings.workingHours.selectInbox': 'בחר תיבה',
    'settings.workingHours.timezone': 'איזור זמן',
    'settings.workingHours.schedule': 'לוח שבועי',
    'settings.workingHours.holidays': 'חגים',
    'settings.workingHours.addHoliday': 'הוסף חג',
    'settings.workingHours.offlineMessage': 'הודעה מחוץ לשעות',
    'settings.workingHours.open': 'פתיחה',
    'settings.workingHours.close': 'סגירה',
    'settings.workingHours.day.mon': 'שני',
    'settings.workingHours.day.tue': 'שלישי',
    'settings.workingHours.day.wed': 'רביעי',
    'settings.workingHours.day.thu': 'חמישי',
    'settings.workingHours.day.fri': 'שישי',
    'settings.workingHours.day.sat': 'שבת',
    'settings.workingHours.day.sun': 'ראשון',
    'settings.sla.title': 'הגדרת SLA',
    'settings.sla.firstResponse': 'יעד תגובה ראשונה (דקות)',
    'settings.sla.resolution': 'יעד סגירה (דקות)',
    'settings.sla.multipliers': 'מקדמי עדיפות',
    'settings.sla.priority.urgent': 'דחוף',
    'settings.sla.priority.high': 'גבוה',
    'settings.sla.priority.medium': 'בינוני',
    'settings.sla.priority.low': 'נמוך',
    'settings.assignment.title': 'הקצאה אוטומטית',
    'settings.assignment.strategy': 'אסטרטגיה',
    'settings.assignment.strategy.roundRobin': 'סבב מחזורי',
    'settings.assignment.strategy.leastLoaded': 'הכי פחות עמוס',
    'settings.assignment.strategy.pinned': 'נציג קבוע',
    'settings.assignment.pinnedAgent': 'נציג קבוע',
    'settings.assignment.skills': 'מיומנויות',
    'settings.assignment.skills.placeholder': 'תגיות מופרדות בפסיק',
    'settings.automations.title': 'אוטומציות',
    'settings.automations.trigger': 'טריגר',
    'settings.automations.conditions': 'תנאים',
    'settings.automations.actions': 'פעולות',
    'settings.automations.addCondition': 'הוסף תנאי',
    'settings.automations.addAction': 'הוסף פעולה',
    'settings.automations.event.created': 'שיחה נוצרה',
    'settings.automations.event.message': 'הודעה התקבלה',
    'settings.automations.event.status': 'סטטוס השתנה',
    'settings.automations.event.labels': 'תוויות השתנו',
    'settings.webhookDeliveries.title': 'משלוחי Webhook',
    'settings.webhookDeliveries.time': 'זמן',
    'settings.webhookDeliveries.url': 'כתובת',
    'settings.webhookDeliveries.event': 'אירוע',
    'settings.webhookDeliveries.status': 'סטטוס',
    'settings.webhookDeliveries.code': 'קוד HTTP',
    'settings.webhookDeliveries.duration': 'משך (ms)',
    'settings.webhookDeliveries.loadMore': 'טען עוד',
    'settings.security.title': 'אבטחה',
    'settings.security.require2fa': 'דרוש אימות דו-שלבי לכל הנציגים',
    'settings.security.idleTimeout': 'יציאה אוטומטית לאחר N דקות חוסר פעילות',
    'settings.security.idleTimeoutHint': 'ברירת מחדל 60 דקות. 0 מבטל יציאה אוטומטית.',
    'settings.security.backendMissing': 'נתיב השרת עדיין לא פרוס.',
    // תקלות ידועות (F-4)
    'settings.knownIssues.title': 'תקלות ידועות',
    'settings.knownIssues.intro':
      'הצהירו על תקלה שמתרחשת עכשיו ("משלוח SMS למספרים ישראליים מתעכב"). כל עוד היא פעילה הבוט מספר עליה ללקוחות ששאלתם קשורה אליה, במקום להוביל אותם לבירור שלא יכול לעזור, וכל נציג רואה אותה מעל התור שלו.',
    'settings.knownIssues.new': '+ הצהרה על תקלה',
    'settings.knownIssues.edit': 'עריכה',
    'settings.knownIssues.fieldTitle': 'כותרת',
    'settings.knownIssues.fieldTitlePlaceholder': 'לדוגמה: משלוח SMS למספרים ישראליים מתעכב',
    'settings.knownIssues.fieldMessage': 'מה לומר ללקוחות',
    'settings.knownIssues.fieldMessagePlaceholder':
      'לדוגמה: הודעות למספרי 05x מגיעות באיחור. אנחנו מטפלים בזה — אין צורך לשלוח שוב או לאפס משהו.',
    'settings.knownIssues.fieldTitleHe': 'כותרת (עברית)',
    'settings.knownIssues.fieldMessageHe': 'מה לומר ללקוחות (עברית)',
    'settings.knownIssues.hebrewHint':
      'לא חובה. לקוחות דוברי עברית יקבלו את הנוסח הזה; אם יישאר ריק הם יקבלו את הנוסח שלמעלה.',
    'settings.knownIssues.fieldEndsAt': 'מסתיים ב- (לא חובה)',
    'settings.knownIssues.endsAtHint':
      'השאירו ריק לתקלה ללא מועד סיום — היא תימשך עד שמישהו יסמן אותה כנפתרה.',
    'settings.knownIssues.save': 'הצהרה על תקלה',
    'settings.knownIssues.saveEdit': 'שמירת השינויים',
    'settings.knownIssues.colIssue': 'תקלה',
    'settings.knownIssues.colWindow': 'חלון זמן',
    'settings.knownIssues.colStatus': 'סטטוס',
    'settings.knownIssues.statusLive': 'פעילה',
    'settings.knownIssues.statusScheduled': 'מתוזמנת',
    'settings.knownIssues.statusEnded': 'חלון הזמן נסגר',
    'settings.knownIssues.statusResolved': 'נפתרה',
    'settings.knownIssues.resolve': 'סימון כנפתרה',
    'settings.knownIssues.resolveConfirm': 'לסמן את “{title}” כנפתרה? הבוט יפסיק להזכיר אותה מיד.',
    'settings.knownIssues.reactivate': 'הפעלה מחדש',
    'settings.knownIssues.windowOpen': 'מ-{from}, ללא מועד סיום',
    'settings.knownIssues.windowUntil': 'מ-{from} עד {to}',
    'settings.knownIssues.liveCount': '{n} פעילות כרגע — הבוט מספר עליהן ללקוחות רלוונטיים.',
    'settings.knownIssues.empty': 'לא הוצהרה אף תקלה. הבוט עונה כאילו הכול תקין.',
    'settings.knownIssues.required': 'חובה למלא גם כותרת וגם הודעה.',
    'settings.knownIssues.invalidWindow': 'מועד הסיום חייב להיות מאוחר ממועד ההתחלה.',
    'settings.knownIssues.pastWindow': 'מועד הסיום הזה כבר עבר, ולכן אף אחד לא יראה את ההודעה.',
    'settings.knownIssues.backendMissing':
      'בשרת הזה עדיין אין נתיב לתקלות ידועות, ולכן אי אפשר להצהיר על תקלה מכאן עד שיעודכן.',
    'settings.knownIssues.readOnly': 'רק מנהל יכול להצהיר על תקלה או לסמן אותה כנפתרה.',
    'settings.branding.logo': 'כתובת לוגו',
    'settings.branding.primaryColor': 'צבע ראשי',
    'settings.branding.brandName': 'שם מותג',
    'onboarding.title': 'ברוכים הבאים ל-ChatOlam',
    'onboarding.step.tenant': 'דייר',
    'onboarding.step.admin': 'מנהל',
    'onboarding.step.inbox': 'תיבה',
    'onboarding.step.seed': 'תוכן',
    'onboarding.step.snippet': 'ווידג׳ט',
    'onboarding.tenant.code': 'קוד דייר',
    'onboarding.tenant.name': 'שם דייר',
    'onboarding.admin.email': 'דוא"ל מנהל ראשי',
    'onboarding.inbox.name': 'שם תיבה ראשית',
    'onboarding.seed.cta': 'זריעת תשובות מוכנות + שאלות נפוצות',
    'onboarding.snippet.title': 'קוד הטמעה לווידג׳ט',
    'onboarding.snippet.copy': 'העתק קוד',
    'onboarding.done': 'סיום',
    'reports.title': 'דוחות',
    'reports.range.7': '7 ימים אחרונים',
    'reports.range.30': '30 ימים אחרונים',
    'reports.range.90': '90 ימים אחרונים',
    'reports.volume.title': 'נפח שיחות',
    'reports.volume.total': 'סה״כ',
    'reports.volume.open': 'פתוח',
    'reports.volume.pending': 'ממתין',
    'reports.volume.resolved': 'נסגר',
    'reports.volume.byChannel': 'לפי ערוץ',
    'reports.volume.byPriority': 'לפי עדיפות',
    'reports.agents.title': 'ביצועי נציגים',
    'reports.agents.agent': 'נציג',
    'reports.agents.assigned': 'משויך',
    'reports.agents.resolved': 'נסגר',
    'reports.agents.avgFirst': 'תגובה ראשונה ממוצעת (דקות)',
    'reports.agents.avgResolution': 'סגירה ממוצעת (דקות)',
    'reports.csat.title': 'שביעות רצון לקוחות',
    'reports.csat.responses': 'תשובות',
    'reports.csat.avg': 'ציון ממוצע',
    'reports.csat.fiveStar': '5 כוכבים',
    'reports.csat.nps': 'NPS',
    'reports.csat.avgLen': 'אורך הערה ממוצע',
    'reports.csat.recentComments': 'הערות אחרונות',
    'reports.export': 'ייצוא שיחות (NDJSON)',
    'reports.histogram.title': 'זמן סגירה',
    'reports.histogram.lt1h': 'פחות משעה',
    'reports.histogram.1to4h': '1-4 שעות',
    'reports.histogram.4to24h': '4-24 שעות',
    'reports.histogram.1to3d': '1-3 ימים',
    'reports.histogram.gt3d': 'מעל 3 ימים',
    'reports.intent.title': 'פילוח כוונות',
    'contacts.anon.show': 'הצג אנונימיים',
    'contacts.anon.hide': 'הסתר אנונימיים',
    'contacts.anon.showing': 'מוצגים מבקרים אנונימיים',
    'contacts.anon.hiddenCount': '{n} אנונימיים מוסתרים',
    'contacts.anon.hiddenSome': 'מוסתרים {n} מבקרים אנונימיים — פניות חד-פעמיות בווידג׳ט ללא דוא״ל או טלפון.',
    'contacts.anon.hiddenUnknown': 'מבקרים אנונימיים מוסתרים — פניות חד-פעמיות בווידג׳ט ללא דוא״ל או טלפון.',
    'contacts.anon.badge': 'אנונימי',
    'contacts.anon.notReady': 'לא ניתן היה להפריד עדיין מבקרים אנונימיים (לאנשי הקשר אין סימון אנונימיות), ולכן כל אנשי הקשר מוצגים.',
    'contacts.empty.noMatch': 'אין אנשי קשר התואמים לסינון.',
    'contacts.empty.allAnon': 'אין אנשי קשר מזוהים התואמים לסינון. {n} מבקרים אנונימיים מוסתרים — הצג אותם, או נקה את הסינון.',
    'contacts.empty.clearFilters': 'נקה סינון',
    'contacts.filter.anonymous': 'אנונימי',
  },
};

export function t(key: string, vars?: Record<string, string | number>): string {
  const table = STRINGS[currentLocale] || STRINGS.en;
  let value = table[key];
  if (value === undefined) value = STRINGS.en[key];
  if (value === undefined) return key;
  if (vars) {
    for (const [k, v] of Object.entries(vars)) {
      value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
    }
  }
  return value;
}

/** The flows review screen. Exported so a toast can link to the place the work
 *  has to be finished instead of only naming it in prose. */
export const FLOWS_SETTINGS_PATH = '/settings/flows';

export interface SavedRuleToast {
  text: string;
  /** Where the remaining step(s) get done. */
  href: string;
  /** False when the save left the agent with work to do (no trigger derived). */
  complete: boolean;
}

/**
 * Copy for the toast after "Fix this answer & teach the AI" saves a RULE.
 *
 * POST /agent/flows/from-correction answers with `triggerDerived`: false means
 * the server could not infer any intent or keyword from the turn, so the draft
 * matches no customer message and approving it would change nothing. The old
 * single string ("approve it in Settings") was a half-truth in that case — the
 * agent was told about one remaining step when there were two, and the reviewer
 * then hits the same empty-trigger guard on approve. Pass the 201 body.
 */
export function savedRuleToast(flow?: { triggerDerived?: boolean } | null): SavedRuleToast {
  // Only an explicit false is "no trigger" — an older backend omits the field
  // entirely, and inventing a warning for it would cry wolf on every save.
  const noTrigger = flow?.triggerDerived === false;
  return {
    text: t(noTrigger ? 'thread.fix.ruleSavedNoTrigger' : 'thread.fix.ruleSaved'),
    href: FLOWS_SETTINGS_PATH,
    complete: !noTrigger,
  };
}

export function useLocale(): { locale: Locale; setLocale: (l: Locale) => void } {
  const [locale, setLocaleState] = useState<Locale>(currentLocale);
  useEffect(() => {
    const fn = () => setLocaleState(currentLocale);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return { locale, setLocale };
}

// Client-side idle auto-logout. Wire from main.tsx or App.tsx:
//   useIdleLogout(timeoutMinutes, () => auth.signOut());
// Reads/writes nothing in this module; pass 0 to disable.
export function useIdleLogout(timeoutMinutes: number, onTimeout: () => void): void {
  useEffect(() => {
    if (!timeoutMinutes || timeoutMinutes <= 0) return;
    const ms = timeoutMinutes * 60 * 1000;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const reset = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        onTimeout();
      }, ms);
    };
    const events: Array<keyof DocumentEventMap> = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
    ];
    events.forEach((ev) => document.addEventListener(ev, reset, { passive: true }));
    reset();
    return () => {
      events.forEach((ev) => document.removeEventListener(ev, reset));
      if (timer) clearTimeout(timer);
    };
  }, [timeoutMinutes, onTimeout]);
}
